import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/utils/api';
import {
  Home, FileText, DollarSign, Wrench, Plus, LogOut,
  Calendar, AlertTriangle, CheckCircle, MessageCircle, Phone,
  CreditCard, Smartphone, Banknote, Receipt
} from 'lucide-react';

const statusColors = {
  paid: 'bg-green-100 text-green-800',
  unpaid: 'bg-yellow-100 text-yellow-800',
  overdue: 'bg-red-100 text-red-800',
  partial: 'bg-blue-100 text-blue-800',
  pending: 'bg-yellow-100 text-yellow-800',
  assigned: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800'
};

// Saudi-localised payment-method label + icon
const paymentMethodLabels = {
  mada:           { label_en: 'Mada',           label_ar: 'مدى',          Icon: CreditCard, color: 'text-blue-700 bg-blue-50 border-blue-100' },
  stc_pay:        { label_en: 'STC Pay',        label_ar: 'STC Pay',      Icon: Smartphone, color: 'text-purple-700 bg-purple-50 border-purple-100' },
  bank_transfer:  { label_en: 'Bank Transfer',  label_ar: 'تحويل بنكي',   Icon: Banknote,   color: 'text-emerald-700 bg-emerald-50 border-emerald-100' },
  cash:           { label_en: 'Cash',           label_ar: 'نقدي',         Icon: Banknote,   color: 'text-gray-700 bg-gray-50 border-gray-200' },
  cheque:         { label_en: 'Cheque',         label_ar: 'شيك',          Icon: Receipt,    color: 'text-amber-700 bg-amber-50 border-amber-100' },
  card:           { label_en: 'Card',           label_ar: 'بطاقة ائتمانية', Icon: CreditCard, color: 'text-indigo-700 bg-indigo-50 border-indigo-100' },
  online:         { label_en: 'Online',         label_ar: 'إلكتروني',     Icon: CreditCard, color: 'text-teal-700 bg-teal-50 border-teal-100' },
};

const PaymentMethodPill = ({ method, isAr }) => {
  if (!method) return <span className="text-gray-400 text-xs">—</span>;
  const cfg = paymentMethodLabels[method.toLowerCase()];
  if (!cfg) return <span className="text-xs">{method}</span>;
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-semibold ${cfg.color}`}>
      <Icon className="h-3 w-3" />
      {isAr ? cfg.label_ar : cfg.label_en}
    </span>
  );
};

export default function TenantPortal() {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [maintDialogOpen, setMaintDialogOpen] = useState(false);
  const [maintForm, setMaintForm] = useState({
    title: '', description: '', priority: 'medium', category: 'general'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await api.getTenantDashboard();
      setData(res.data);
    } catch (error) {
      console.error('Failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitMaintenance = async (e) => {
    e.preventDefault();
    if (!data?.unit) return;
    try {
      await api.tenantSubmitMaintenance({
        ...maintForm,
        unit_id: data.unit.id,
        property_id: data.unit.property_id
      });
      setMaintDialogOpen(false);
      setMaintForm({ title: '', description: '', priority: 'medium', category: 'general' });
      fetchData();
    } catch (error) {
      console.error('Failed:', error);
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';

  const tabs = [
    { id: 'dashboard', icon: Home, label: t('dashboard') },
    { id: 'lease', icon: FileText, label: t('leases') },
    { id: 'payments', icon: DollarSign, label: t('payments') },
    { id: 'maintenance', icon: Wrench, label: t('maintenance') }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-gray-900">REVA Tenant Portal</h1>
            <p className="text-sm text-gray-500">{user?.full_name}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant={i18n.language === 'en' ? 'default' : 'outline'} size="sm" onClick={() => changeLanguage('en')}>EN</Button>
            <Button variant={i18n.language === 'ar' ? 'default' : 'outline'} size="sm" onClick={() => changeLanguage('ar')}>AR</Button>
            <Button variant="outline" size="sm" onClick={logout}><LogOut className="h-4 w-4 mr-1" /> {t('logout')}</Button>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-1">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="h-4 w-4" /> {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {!data?.tenant ? (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No Tenant Profile Found</h3>
              <p className="text-gray-500 mt-2">Your account is not linked to a tenant profile yet. Please contact your property manager.</p>
              <Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard')}>Go to Manager Dashboard</Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && (
              <div className="space-y-6">
                {/* Quick-actions banner — WhatsApp + Pay Now */}
                {(() => {
                  const isAr = i18n.language === 'ar';
                  // Look up an overdue payment for the prominent "Pay Now" CTA
                  const overdue = (data.payments || []).find(p => p.status === 'overdue' || p.status === 'unpaid');
                  // Property-manager phone — falls back to a generic support line if not on the unit
                  const pmPhoneRaw = data.property_manager_phone || data.unit?.contact_phone || data.organization?.contact_phone || '';
                  const pmPhoneClean = (pmPhoneRaw || '').replace(/\D/g, '');
                  const waMessage = encodeURIComponent(
                    isAr
                      ? `مرحباً، أنا ${data.tenant?.full_name || ''} مستأجر الوحدة ${data.unit?.unit_number || ''}.`
                      : `Hi, I'm ${data.tenant?.full_name || ''}, the tenant of unit ${data.unit?.unit_number || ''}.`
                  );
                  const waUrl = pmPhoneClean ? `https://wa.me/${pmPhoneClean}?text=${waMessage}` : null;
                  const telUrl = pmPhoneRaw ? `tel:${pmPhoneRaw}` : null;
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Overdue rent → Pay Now CTA */}
                      {overdue ? (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center justify-between gap-3" data-testid="tenant-pay-now-banner">
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 text-red-700 text-xs font-bold uppercase tracking-wider mb-0.5">
                              <AlertTriangle className="h-3.5 w-3.5" />
                              {isAr ? 'دفعة مستحقة' : 'Payment due'}
                            </div>
                            <div className="text-lg font-bold text-gray-900">{overdue.amount?.toLocaleString()} SAR</div>
                            <div className="text-[11px] text-gray-500">{isAr ? 'تاريخ الاستحقاق' : 'Due'}: {formatDate(overdue.due_date)}</div>
                          </div>
                          <div className="flex flex-col gap-1.5 items-end">
                            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white rounded-lg" data-testid="tenant-pay-now-btn">
                              <CreditCard className="h-3.5 w-3.5 me-1" />
                              {isAr ? 'ادفع الآن' : 'Pay Now'}
                            </Button>
                            <div className="flex items-center gap-1 text-[10px] text-gray-500">
                              <CreditCard className="h-3 w-3" /> {isAr ? 'مدى' : 'Mada'}
                              <span className="text-gray-300">·</span>
                              <Smartphone className="h-3 w-3" /> STC Pay
                              <span className="text-gray-300">·</span>
                              <Banknote className="h-3 w-3" /> {isAr ? 'تحويل' : 'Bank'}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3">
                          <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                          <div>
                            <div className="text-sm font-bold text-emerald-900">{isAr ? 'كل دفعاتك مسددة' : 'All payments up to date'}</div>
                            <div className="text-[11px] text-emerald-700/80">{isAr ? 'شكراً لك. لا توجد مبالغ مستحقة حالياً.' : 'Thank you. No outstanding payments.'}</div>
                          </div>
                        </div>
                      )}

                      {/* Contact property manager — WhatsApp + phone */}
                      <div className="p-4 bg-white border border-gray-200 rounded-xl flex items-center justify-between gap-3" data-testid="tenant-contact-pm">
                        <div className="min-w-0">
                          <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-0.5">{isAr ? 'تواصل مع إدارة العقار' : 'Contact your property manager'}</div>
                          <div className="text-sm font-semibold text-gray-900 truncate">{data.property?.name || data.unit?.property_name || (isAr ? 'إدارة العقار' : 'Property Management')}</div>
                          <div className="text-[11px] text-gray-500">{isAr ? 'متاح من الأحد إلى الخميس · 9 ص – 6 م' : 'Sun–Thu · 9am–6pm KSA'}</div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {waUrl ? (
                            <a href={waUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#25D366] hover:bg-[#1da851] text-white rounded-lg text-xs font-semibold" data-testid="tenant-whatsapp-pm">
                              <MessageCircle className="h-3.5 w-3.5" />
                              WhatsApp
                            </a>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-400 rounded-lg text-xs font-semibold cursor-not-allowed">
                              <MessageCircle className="h-3.5 w-3.5" />
                              WhatsApp
                            </span>
                          )}
                          {telUrl && (
                            <a href={telUrl} className="inline-flex items-center justify-center w-9 h-9 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg" title={pmPhoneRaw} data-testid="tenant-call-pm">
                              <Phone className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <Home className="h-5 w-5 text-blue-600 mb-2" />
                      <div className="text-lg font-bold">{data.unit?.unit_number || '-'}</div>
                      <div className="text-sm text-gray-500">{data.unit?.unit_type} - {data.unit?.size_sqm} sqm</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <DollarSign className="h-5 w-5 text-green-600 mb-2" />
                      <div className="text-lg font-bold">{data.lease?.monthly_rent?.toLocaleString()} SAR</div>
                      <div className="text-sm text-gray-500">{t('monthlyRent')}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <Calendar className="h-5 w-5 text-purple-600 mb-2" />
                      <div className="text-lg font-bold">{formatDate(data.lease?.end_date)}</div>
                      <div className="text-sm text-gray-500">Lease Expires</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Payments */}
                <Card>
                  <CardHeader><CardTitle className="text-base">Recent Payments</CardTitle></CardHeader>
                  <CardContent>
                    {data.payments?.length > 0 ? (
                      <div className="space-y-2">
                        {data.payments.slice(0, 5).map(p => (
                          <div key={p.id} className="flex justify-between items-center py-2 border-b last:border-0">
                            <div>
                              <div className="font-medium">{p.amount?.toLocaleString()} SAR</div>
                              <div className="text-xs text-gray-500">Due: {formatDate(p.due_date)}</div>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[p.status]}`}>
                              {t(p.status)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-gray-500">No payment records yet.</p>}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Lease Tab */}
            {activeTab === 'lease' && (
              <Card>
                <CardHeader><CardTitle>Lease Details</CardTitle></CardHeader>
                <CardContent>
                  {data.lease ? (
                    <div className="grid grid-cols-2 gap-6">
                      <div><Label className="text-gray-500">{t('monthlyRent')}</Label><div className="text-lg font-bold">{data.lease.monthly_rent?.toLocaleString()} SAR</div></div>
                      <div><Label className="text-gray-500">{t('securityDeposit')}</Label><div className="text-lg font-bold">{data.lease.security_deposit?.toLocaleString()} SAR</div></div>
                      <div><Label className="text-gray-500">{t('startDate')}</Label><div className="font-medium">{formatDate(data.lease.start_date)}</div></div>
                      <div><Label className="text-gray-500">{t('endDate')}</Label><div className="font-medium">{formatDate(data.lease.end_date)}</div></div>
                      <div><Label className="text-gray-500">{t('paymentDay')}</Label><div className="font-medium">Day {data.lease.payment_day} of each month</div></div>
                      <div><Label className="text-gray-500">{t('status')}</Label><div><span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[data.lease.status] || 'bg-gray-100'}`}>{t(data.lease.status)}</span></div></div>
                      <div><Label className="text-gray-500">Unit</Label><div className="font-medium">{data.unit?.unit_number} ({data.unit?.unit_type})</div></div>
                      <div><Label className="text-gray-500">Floor</Label><div className="font-medium">{data.unit?.floor || '-'}</div></div>
                    </div>
                  ) : <p className="text-gray-500">No active lease found.</p>}
                </CardContent>
              </Card>
            )}

            {/* Payments Tab */}
            {activeTab === 'payments' && (
              <Card>
                <CardHeader><CardTitle>{t('payments')}</CardTitle></CardHeader>
                <CardContent>
                  {data.payments?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('amount')}</TableHead>
                          <TableHead>{t('dueDate')}</TableHead>
                          <TableHead>{t('paidDate')}</TableHead>
                          <TableHead>{t('status')}</TableHead>
                          <TableHead>{t('paymentMethod')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.payments.map(p => (
                          <TableRow key={p.id}>
                            <TableCell className="font-medium">{p.amount?.toLocaleString()} SAR</TableCell>
                            <TableCell>{formatDate(p.due_date)}</TableCell>
                            <TableCell>{formatDate(p.paid_date)}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[p.status]}`}>{t(p.status)}</span>
                            </TableCell>
                            <TableCell><PaymentMethodPill method={p.payment_method} isAr={i18n.language === 'ar'} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : <p className="text-gray-500">No payment records.</p>}
                </CardContent>
              </Card>
            )}

            {/* Maintenance Tab */}
            {activeTab === 'maintenance' && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <Dialog open={maintDialogOpen} onOpenChange={setMaintDialogOpen}>
                    <DialogTrigger asChild>
                      <Button><Plus className="mr-2 h-4 w-4" /> {t('addMaintenanceRequest')}</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>{t('addMaintenanceRequest')}</DialogTitle></DialogHeader>
                      <form onSubmit={handleSubmitMaintenance} className="space-y-4">
                        <div><Label>{t('title')}*</Label><Input value={maintForm.title} onChange={(e) => setMaintForm({ ...maintForm, title: e.target.value })} required /></div>
                        <div><Label>{t('description')}*</Label><textarea className="w-full border rounded-md p-3 text-sm min-h-[80px]" value={maintForm.description} onChange={(e) => setMaintForm({ ...maintForm, description: e.target.value })} required /></div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>{t('priority')}</Label>
                            <Select value={maintForm.priority} onValueChange={(v) => setMaintForm({ ...maintForm, priority: v })}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {['low', 'medium', 'high', 'urgent'].map(p => <SelectItem key={p} value={p}>{t(p)}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>{t('category')}</Label>
                            <Select value={maintForm.category} onValueChange={(v) => setMaintForm({ ...maintForm, category: v })}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {['plumbing', 'electrical', 'hvac', 'cleaning', 'painting', 'general'].map(c => (
                                  <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Button type="submit" className="w-full">{t('save')}</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>

                <Card>
                  <CardHeader><CardTitle>{t('maintenance')}</CardTitle></CardHeader>
                  <CardContent>
                    {data.maintenance_requests?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('title')}</TableHead>
                            <TableHead>{t('category')}</TableHead>
                            <TableHead>{t('priority')}</TableHead>
                            <TableHead>{t('status')}</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.maintenance_requests.map(req => (
                            <TableRow key={req.id}>
                              <TableCell className="font-medium">{req.title}</TableCell>
                              <TableCell className="capitalize">{req.category}</TableCell>
                              <TableCell>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  req.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                                  req.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>{t(req.priority)}</span>
                              </TableCell>
                              <TableCell>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[req.status] || 'bg-gray-100'}`}>
                                  {req.status === 'in_progress' ? t('inProgress') : t(req.status)}
                                </span>
                              </TableCell>
                              <TableCell>{formatDate(req.created_at)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : <p className="text-gray-500">No maintenance requests yet.</p>}
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
